import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";

// Eagerly load the landing page for fast initial render
import Index from "./pages/Index";

// Lazy load all other pages to reduce initial bundle size
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Goals = lazy(() => import("./pages/Goals"));
const Progress = lazy(() => import("./pages/Progress"));
const Routines = lazy(() => import("./pages/Routines"));
const Habits = lazy(() => import("./pages/Habits"));
const Focus = lazy(() => import("./pages/Focus"));
const Community = lazy(() => import("./pages/Community"));
const Settings = lazy(() => import("./pages/Settings"));
const Mood = lazy(() => import("./pages/Mood"));
const VoiceCoach = lazy(() => import("./pages/VoiceCoach"));
const Admin = lazy(() => import("./pages/Admin"));
const Profile = lazy(() => import("./pages/Profile"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Vault = lazy(() => import("./pages/Vault"));
const ForgeMasters = lazy(() => import("./pages/ForgeMasters"));
const HallOfForged = lazy(() => import("./pages/HallOfForged"));
const Reflect = lazy(() => import("./pages/Reflect"));
const Journal = lazy(() => import("./pages/Journal"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="goals" element={<Goals />} />
                <Route path="progress" element={<Progress />} />
                <Route path="routines" element={<Routines />} />
                <Route path="habits" element={<Habits />} />
                <Route path="focus" element={<Focus />} />
                <Route path="community" element={<Community />} />
                <Route path="settings" element={<Settings />} />
                <Route path="mood" element={<Mood />} />
                <Route path="voice" element={<VoiceCoach />} />
                <Route path="admin" element={<Admin />} />
                <Route path="profile" element={<Profile />} />
                <Route path="profile/:userId" element={<PublicProfile />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="vault" element={<Vault />} />
                <Route path="forge-masters" element={<ForgeMasters />} />
                <Route path="hall-of-forged" element={<HallOfForged />} />
                <Route path="reflect" element={<Reflect />} />
                <Route path="journal" element={<Journal />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
